import { Button, Dialog, LogoPay, XIcon } from "@au-schein-de/au-ui-library";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useTranslation } from "react-i18next";
import { TransformedImage } from "~/components/transformed-image.component";
import { DrAnsayPayModalPattern } from "./dransay-pay-modal-pattern.component";

interface DrAnsayPayLearnMoreDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	overlayClassName?: string;
}

interface BenefitRowProps {
	iconSrc: string;
	title: string;
	description: string;
	testId: string;
}

function BenefitRow({ iconSrc, title, description, testId }: BenefitRowProps) {
	return (
		<div className="flex min-h-12 gap-2" data-testid={testId}>
			<div className="flex h-12 shrink-0 items-start pt-2">
				<TransformedImage
					src={iconSrc}
					alt=""
					width={32}
					height={32}
					className="size-8 object-contain"
					loading="lazy"
				/>
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<p className="text-body-sm-emphasis text-gray-900">{title}</p>
				<p className="text-body-sm text-gray-700">{description}</p>
			</div>
		</div>
	);
}

/** DrAnsay Pay education overlay — desktop modal, mobile bottom drawer. */
export function DrAnsayPayLearnMoreDialog({
	open,
	onOpenChange,
	overlayClassName,
}: DrAnsayPayLearnMoreDialogProps) {
	const { t } = useTranslation("auth");
	const benefits: BenefitRowProps[] = [
		{
			iconSrc: "/images/auth/dransay-pay/Geldcoin-1.png",
			title: t("dransayPay.modal.benefits.oneTimePayment.title"),
			description: t("dransayPay.modal.benefits.oneTimePayment.description"),
			testId: "dransay-pay-learn-more-benefit-one-time-payment",
		},
		{
			iconSrc: "/images/auth/dransay-pay/bolt.png",
			title: t("dransayPay.modal.benefits.fasterProcess.title"),
			description: t("dransayPay.modal.benefits.fasterProcess.description"),
			testId: "dransay-pay-learn-more-benefit-faster-process",
		},
		{
			iconSrc: "/images/auth/dransay-pay/verification.png",
			title: t("dransayPay.modal.benefits.simpleSecure.title"),
			description: t("dransayPay.modal.benefits.simpleSecure.description"),
			testId: "dransay-pay-learn-more-benefit-simple-secure",
		},
	];
	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay
					className={`top-[110px] md:top-[88px] ${overlayClassName ?? ""}`}
				/>
				<DialogPrimitive.Content
					data-testid="dransay-pay-learn-more-dialog"
					className="fixed z-(--z-index-dialog-content) grid w-full max-w-none gap-6 overflow-y-auto bg-white outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 bottom-0 top-auto flex translate-y-0 flex-col items-center overflow-hidden rounded-t-2xl p-0 max-h-screen focus:outline-none focus-visible:outline-none md:left-[50%] md:bottom-auto md:top-[50%] md:w-[570px] md:max-w-[570px] md:max-h-[calc(96vh-88px)] md:translate-x-[-50%] md:translate-y-[-50%] md:gap-0 md:rounded-2xl md:p-0"
					onInteractOutside={(event) => {
						event.preventDefault();
					}}
					onEscapeKeyDown={(event) => {
						event.preventDefault();
					}}
				>
					<div className="relative h-[130px] w-full shrink-0 overflow-hidden bg-ansay-900 md:h-[183px]">
						<DrAnsayPayModalPattern className="pointer-events-none absolute right-0 top-0 h-[145px] w-[165px] text-white opacity-[0.06] md:h-[189px] md:w-[217px]" />
						<LogoPay
							title="DrAnsay Pay"
							variant="light"
							size={33}
							className="absolute left-6 top-6 md:hidden"
							data-testid="dransay-pay-learn-more-logo-mobile"
						/>
						<LogoPay
							title="DrAnsay Pay"
							variant="light"
							size={40}
							className="absolute left-10 top-10 hidden md:block"
							data-testid="dransay-pay-learn-more-logo"
						/>
						<p className="absolute left-6 top-[69px] right-14 z-10 text-h4 text-white md:bottom-10 md:left-10 md:top-auto md:max-w-[371px] md:text-h3">
							<span>{t("dransayPay.modal.headerIntroducing")} </span>
							<span className="text-ansay-light">
								{t("dransayPay.modal.headerAccent")}
							</span>
						</p>
						<Dialog.Close
							className="absolute right-4 top-4 flex size-10 cursor-pointer items-center justify-center rounded-full bg-ansay-400 outline-none transition-colors hover:bg-[#358f81] focus:outline-none focus-visible:outline-none md:right-6 md:top-6"
							data-testid="dransay-pay-learn-more-close-x"
						>
							<XIcon.Raw className="size-5 text-white" />
							<span className="sr-only">{t("dransayPay.modal.close")}</span>
						</Dialog.Close>
					</div>
					<div className="flex w-full flex-col gap-6 px-4 pb-4 md:px-10 md:pt-6 md:pb-10">
						<div className="flex flex-col gap-2 md:w-[490px] md:gap-3">
							<DialogPrimitive.Title className="text-h5 text-gray-900">
								{t("dransayPay.modal.headline")}
							</DialogPrimitive.Title>
							<DialogPrimitive.Description asChild>
								<div className="flex flex-col gap-2 text-body-sm text-gray-700 md:w-[490px] md:gap-3 md:text-body">
									<p>{t("dransayPay.modal.paragraph1")}</p>
									<p>{t("dransayPay.modal.paragraph2")}</p>
								</div>
							</DialogPrimitive.Description>
						</div>
						<div className="flex flex-col gap-3 md:w-[490px] md:gap-3">
							{benefits.map((benefit) => (
								<BenefitRow key={benefit.testId} {...benefit} />
							))}
						</div>
						<Dialog.Close asChild>
							<Button
								size="md"
								variant="primary"
								className="h-14 w-full md:w-[490px]"
								data-testid="dransay-pay-learn-more-close"
							>
								{t("dransayPay.modal.cta")}
							</Button>
						</Dialog.Close>
					</div>
				</DialogPrimitive.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
